import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import {
  Badge,
  Button,
  confirm,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  FormInput,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  ExternalLinkIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  UserCogIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import type { EventStaffDTO } from "../../domain/contracts";
import {
  useAssignEventStaff,
  useEventStaffQuery,
  useUnassignEventStaff,
  useUpdateEventStaffAssignment,
} from "../api/queries";

const STAFF_LOOKUP = `${config.apiUrl}/staff/lookup`;

type StaffLookupItem = {
  id: string;
  name: string;
  defaultRoleLabel?: string | null;
};

const AssignFormSchema = z.object({
  staffId: z.string().min(1, "ເລືອກ Staff"),
  roleLabel: z.string().optional(),
  notes: z.string().optional(),
});

type AssignFormValues = z.infer<typeof AssignFormSchema>;

const EditAssignmentSchema = z.object({
  roleLabel: z.string().optional(),
  notes: z.string().optional(),
});

type EditAssignmentValues = z.infer<typeof EditAssignmentSchema>;

function displayRole(row: EventStaffDTO) {
  return row.roleLabel ?? row.defaultRoleLabel ?? "—";
}

type AssignStaffDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  assignedStaffIds: string[];
};

function AssignStaffDialog({
  open,
  onOpenChange,
  eventId,
  assignedStaffIds,
}: AssignStaffDialogProps) {
  const assignStaff = useAssignEventStaff(eventId);

  const methods = RHF.useForm<AssignFormValues>({
    resolver: zodResolver(AssignFormSchema),
    defaultValues: { staffId: "", roleLabel: "", notes: "" },
  });

  useEffect(() => {
    if (!open) return;
    methods.reset({ staffId: "", roleLabel: "", notes: "" });
  }, [open, methods]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>ມອບໝາຍ Staff</DialogTitle>
        </DialogHeader>
        <FormRoot
          methods={methods}
          onSubmit={methods.handleSubmit(async (values) => {
            if (assignedStaffIds.includes(values.staffId)) return;
            await assignStaff.mutateAsync({
              staffId: values.staffId,
              roleLabel: values.roleLabel?.trim() || undefined,
              notes: values.notes?.trim() || undefined,
            });
            onOpenChange(false);
          })}
        >
          <div className="space-y-3">
            <FormInfiniteCombobox<StaffLookupItem>
              name="staffId"
              label="Staff"
              requiredMark
              placeholder="ຄົ້ນຫາ Staff..."
              queryKey={["performance-staff", "lookup", "assign"]}
              queryFn={({ search, pageParam }) =>
                fetchLookupForInfinite<StaffLookupItem>(STAFF_LOOKUP, {
                  search,
                  pageParam,
                })
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem<StaffLookupItem>(STAFF_LOOKUP, id)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
            />
            <FormInput
              name="roleLabel"
              label="ບົດບາດໃນ Event (override)"
              placeholder="ຖ້າບໍ່ປ້ອນ ຈະໃຊ້ບົດບາດເລີ່ມຕົ້ນ"
            />
            <FormTextarea name="notes" label="ຫມາຍເຫດ" />
            <p className="text-muted-foreground text-xs">
              ຍັງບໍ່ມີລາຍຊື່?{" "}
              <Link
                to="/app/performance/staff/create"
                className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                target="_blank"
              >
                ເພີ່ມ Staff ໃໝ່
                <ExternalLinkIcon className="size-3" />
              </Link>
            </p>
          </div>
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={assignStaff.isPending}>
              ມອບໝາຍ
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}

type EditAssignmentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  assignment: EventStaffDTO | null;
};

function EditAssignmentDialog({
  open,
  onOpenChange,
  eventId,
  assignment,
}: EditAssignmentDialogProps) {
  const updateAssignment = useUpdateEventStaffAssignment(eventId);

  const methods = RHF.useForm<EditAssignmentValues>({
    resolver: zodResolver(EditAssignmentSchema),
    defaultValues: { roleLabel: "", notes: "" },
  });

  useEffect(() => {
    if (!open || !assignment) return;
    methods.reset({
      roleLabel: assignment.roleLabel ?? "",
      notes: assignment.notes ?? "",
    });
  }, [open, assignment, methods]);

  if (!assignment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>ແກ້ການມອບໝາຍ: {assignment.displayName}</DialogTitle>
        </DialogHeader>
        <FormRoot
          methods={methods}
          onSubmit={methods.handleSubmit(async (values) => {
            await updateAssignment.mutateAsync({
              assignmentId: assignment.id,
              input: {
                roleLabel: values.roleLabel?.trim() || null,
                notes: values.notes?.trim() || null,
              },
            });
            onOpenChange(false);
          })}
        >
          <div className="space-y-3">
            <FormInput name="roleLabel" label="ບົດບາດໃນ Event" />
            <FormTextarea name="notes" label="ຫມາຍເຫດ" />
          </div>
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={updateAssignment.isPending}>
              ບັນທຶກ
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}

type EventStaffAssignSectionProps = {
  eventId: string;
};

export function EventStaffAssignSection({
  eventId,
}: EventStaffAssignSectionProps) {
  const canManage = useActionPermission(["performance-payouts:create"]);
  const staffQuery = useEventStaffQuery(eventId);
  const unassignStaff = useUnassignEventStaff(eventId);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    assignment: EventStaffDTO | null;
  }>({ open: false, assignment: null });

  const staff = staffQuery.data ?? [];
  const activeStaff = staff.filter((row) => row.isActive);
  const assignedStaffIds = activeStaff.map((row) => row.staffId);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div className="flex items-center gap-2">
          <UserCogIcon className="size-4 text-primary" />
          <span className="font-semibold text-sm uppercase tracking-widest">
            ທີມ Staff
          </span>
        </div>
        {canManage ? (
          <Button size="sm" onClick={() => setAssignOpen(true)}>
            <PlusIcon className="size-4" /> ມອບໝາຍ Staff
          </Button>
        ) : null}
      </div>

      <div className="flex-1 p-5">
        {staffQuery.isLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : activeStaff.length === 0 ? (
          <Empty className="py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <UserCogIcon className="size-12 text-muted-foreground" />
              </EmptyMedia>
              <EmptyTitle>ຍັງບໍ່ມີ Staff ທີ່ມອບໝາຍ</EmptyTitle>
              <EmptyDescription>
                ເລືອກ Staff ຈາກລາຍຊື່ກາງ ກ່ອນສ້າງໃບເບີກຈ່າຍ
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground text-xs uppercase tracking-wide">
                  <th className="pb-2 pr-3 font-medium">ຊື່</th>
                  <th className="pb-2 pr-3 font-medium">ບົດບາດ</th>
                  <th className="pb-2 pr-3 font-medium">ບັນຊີ</th>
                  <th className="pb-2 font-medium text-right">ຈັດການ</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((row) => (
                  <tr
                    key={row.id}
                    className={
                      row.isActive ? "border-b" : "border-b opacity-50"
                    }
                  >
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{row.displayName}</span>
                        {!row.isActive ? (
                          <Badge variant="secondary">ຍົກເລີກແລ້ວ</Badge>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-muted-foreground">
                      {displayRole(row)}
                    </td>
                    <td className="py-3 pr-3 text-muted-foreground text-xs">
                      {row.bankAccount
                        ? `${row.bankName ?? ""} · ${row.bankAccount}`
                        : "—"}
                    </td>
                    <td className="py-3 text-right">
                      {canManage && row.isActive ? (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8"
                            onClick={() =>
                              setEditDialog({ open: true, assignment: row })
                            }
                          >
                            <PencilIcon className="size-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-destructive hover:bg-destructive/10"
                            disabled={unassignStaff.isPending}
                            onClick={async () => {
                              const ok = await confirm({
                                title: "ຍົກເລີກການມອບໝາຍ",
                                description: `ຢືນຢັນຍົກເລີກ ${row.displayName} ອອກຈາກ Event ນີ້?`,
                                actionText: "ຍົກເລີກ",
                                ActionProps: { variant: "destructive" },
                              });
                              if (ok) unassignStaff.mutate(row.id);
                            }}
                          >
                            <Trash2Icon className="size-4" />
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AssignStaffDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        eventId={eventId}
        assignedStaffIds={assignedStaffIds}
      />

      <EditAssignmentDialog
        open={editDialog.open}
        onOpenChange={(open) => setEditDialog((prev) => ({ ...prev, open }))}
        eventId={eventId}
        assignment={editDialog.assignment}
      />
    </div>
  );
}
